import { z } from 'zod';
import { RunStatusSchema } from './run.js';
/**
 * Scheduled workflow runs (DEV-17470). A `WorkflowSchedule` is a cron
 * expression + a fixed trigger input bound to one workflow. The api's
 * `WorkflowScheduleTickService` claims due rows every 30 s and calls
 * `RunService.create` with `trigger: 'SCHEDULED'`, so the resulting runs
 * look like any other run in the Runs tab except for their trigger.
 *
 * Mirrors the Prisma `WorkflowSchedule` model.
 */
/** Minimum cadence a schedule may fire at. Guards against `* * * * * *`. */
export const workflowScheduleMinIntervalMs = 60_000;
/**
 * Minimum cadence for a schedule on a Formant-managed workflow (DEV-17471):
 * each fire is an org-wide gather with real model spend. The api reads
 * `CORTEX_MANAGED_SCHEDULE_MIN_INTERVAL_MS` over this default; the console
 * keeps its presets to it.
 */
export const managedWorkflowScheduleMinIntervalMs = 15 * 60_000;
/**
 * Why the platform, not a person, set `enabled = false` (DEV-17471).
 * `workflow-disabled` while its workflow is disabled (cleared when it is
 * enabled again), `repeated-errors` when the failure breaker trips,
 * `view-removed` when a managed instance's view is gone. Null while enabled
 * and for a pause a person asked for: in-process callers (managed
 * provisioning, the Daily Brief register) re-enable only system pauses.
 */
export const WorkflowSchedulePausedReasonSchema = z.enum([
    'workflow-disabled',
    'repeated-errors',
    'view-removed',
]);
/**
 * `lastError` text of a managed workflow's schedule after a failed run. The
 * run's own error can describe org-wide data, so every org member sees only
 * the class of failure (`no-identity` keeps its own line: the product copy keys on it).
 */
export const managedScheduleRunFailedCopy = 'An update failed. Formant has been notified.';
/** `lastError` text once the failure breaker has stopped a managed workflow's schedule. */
export const managedScheduleBreakerCopy = 'Updates paused after repeated errors. Formant has been notified.';
// ── Tags (DEV-17502) ──────────────────────────────────────────────────────
/** Most key/value pairs one schedule may carry. */
export const scheduleTagMaxPairs = 10;
/** Longest tag key, after trimming. */
export const scheduleTagKeyMaxLength = 64;
/** Longest tag value, after trimming. */
export const scheduleTagValueMaxLength = 200;
/**
 * Normalize tags the way they are stored: keys trimmed and lowercased,
 * values trimmed, pairs with an empty key or value dropped. Idempotent.
 * Count and length limits are enforced by {@link ScheduleTagsSchema}, not
 * here, so a read-side caller can normalize anything without throwing.
 */
export function normalizeScheduleTags(raw) {
    const out = {};
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return out;
    for (const [rawKey, rawValue] of Object.entries(raw)) {
        if (typeof rawValue !== 'string')
            continue;
        const key = rawKey.trim().toLowerCase();
        const value = rawValue.trim();
        if (!key || !value)
            continue;
        out[key] = value;
    }
    return out;
}
/**
 * Tags as a client sends them. Parsing normalizes (see
 * {@link normalizeScheduleTags}), then caps the count and the lengths and
 * refuses a `:` in a key, since the list filter spells a pair `key:value`
 * and splits on the first colon.
 */
export const ScheduleTagsSchema = z
    .record(z.string().max(scheduleTagKeyMaxLength), z.string().max(scheduleTagValueMaxLength))
    .transform((raw) => normalizeScheduleTags(raw))
    .refine((tags) => Object.keys(tags).length <= scheduleTagMaxPairs, {
    message: `A schedule carries at most ${scheduleTagMaxPairs} tags`,
})
    .refine((tags) => Object.keys(tags).every((key) => !key.includes(':')), {
    message: 'A tag key cannot contain ":"',
});
/**
 * Parse one `tag` query value (`key:value`) into a pair, normalized like a
 * stored tag. Null when the text is not a pair: no colon, or an empty key
 * or value. The value keeps any later colons (`url:https://…`).
 */
export function parseScheduleTagParam(raw) {
    const idx = raw.indexOf(':');
    if (idx < 0)
        return null;
    const key = raw.slice(0, idx).trim().toLowerCase();
    const value = raw.slice(idx + 1).trim();
    if (!key || !value)
        return null;
    return { key, value };
}
/** `key:value` for a pair, the inverse of {@link parseScheduleTagParam}. */
export function formatScheduleTagParam(pair) {
    return `${pair.key}:${pair.value}`;
}
/** A tag map as the `tag` query values of the list filter. */
export function scheduleTagsToPairs(tags) {
    return Object.entries(normalizeScheduleTags(tags)).map(([key, value]) => ({ key, value }));
}
/**
 * The list filter's rule: a schedule matches when every one of its tags is
 * among the given pairs, so a schedule's tags are a SUBSET of the filter.
 * An untagged (org-wide) schedule matches every filter unless
 * `includeUntagged` is false; a schedule tagged for another site, or for
 * a key the filter does not name, never matches. With no pairs given,
 * every schedule matches (subject to `includeUntagged`).
 */
export function scheduleTagsMatch(tags, pairs, opts = {}) {
    const own = normalizeScheduleTags(tags);
    const keys = Object.keys(own);
    if (keys.length === 0)
        return opts.includeUntagged !== false;
    if (pairs.length === 0)
        return true;
    const allowed = new Map();
    for (const pair of pairs) {
        const key = pair.key.trim().toLowerCase();
        if (!allowed.has(key))
            allowed.set(key, new Set());
        allowed.get(key)?.add(pair.value.trim());
    }
    return keys.every((key) => allowed.get(key)?.has(own[key]) ?? false);
}
/** The keys a fire adds to the run input besides the tags themselves. */
export const scheduleInputKeys = {
    tags: 'scheduleTags',
    id: 'scheduleId',
    name: 'scheduleName',
};
/**
 * The run input a schedule fire sends: the schedule's `input`, plus
 * `scheduleTags` (always set, `{}` when the schedule has none), each tag as
 * a top-level key when the input does not already have that key, and
 * `scheduleId` / `scheduleName` when the input does not already carry them.
 * The input's own keys always win, so a workflow whose input names `site`
 * is never overridden by a tag. Pure: the given input is not changed.
 */
export function mergeScheduleTagsIntoInput(input, schedule) {
    const tags = normalizeScheduleTags(schedule.tags);
    const out = { ...input, [scheduleInputKeys.tags]: tags };
    for (const [key, value] of Object.entries(tags)) {
        if (!(key in input))
            out[key] = value;
    }
    if (!(scheduleInputKeys.id in input))
        out[scheduleInputKeys.id] = schedule.id;
    if (!(scheduleInputKeys.name in input))
        out[scheduleInputKeys.name] = schedule.name;
    return out;
}
export const WorkflowScheduleSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    workflowId: z.string(),
    /** Pin to a specific version; null = the workflow's currentVersion at fire time. */
    workflowVersionId: z.string().nullable(),
    name: z.string(),
    /** Trigger payload handed to every run. Validated against the version's inputSchema at fire time. */
    input: z.record(z.string(), z.unknown()),
    /**
     * Site / customer tags (DEV-17502): `{}` for an org-wide schedule. Injected
     * into every run's input (see {@link mergeScheduleTagsIntoInput}) and the
     * key of the list filter (see {@link scheduleTagsMatch}).
     */
    tags: z.record(z.string(), z.string()),
    /** 5-field (minute) or 6-field (second) crontab. */
    cron: z.string(),
    /** IANA zone the cron is evaluated in. */
    timezone: z.string(),
    enabled: z.boolean(),
    /** Skip a tick while the previous run from this schedule is still in flight. */
    preventOverlap: z.boolean(),
    /** Auto-disable after this many consecutive failed fires / failed runs. */
    maxConsecutiveFailures: z.number().int().nonnegative(),
    consecutiveFailures: z.number().int().nonnegative(),
    lastRunId: z.string().nullable(),
    lastRunAt: z.date().nullable(),
    lastRunStatus: RunStatusSchema.nullable(),
    /** Message from the most recent failed fire; cleared on the next success. */
    lastError: z.string().nullable(),
    /** Set with `enabled: false` by the platform; null for a person's pause. */
    pausedReason: WorkflowSchedulePausedReasonSchema.nullable(),
    /** When a run was last created for this schedule; a skipped slot never moves it. */
    lastFiredAt: z.date().nullable(),
    /** The last run whose terminal status has been counted toward the breaker. */
    lastAccountedRunId: z.string().nullable(),
    /** Null when disabled (the tick never claims a null nextRunAt). */
    nextRunAt: z.date().nullable(),
    /** Keycloak subject (or service identity) that created the schedule. */
    createdBy: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
/**
 * Loose cron shape check. Real validation (field ranges, step syntax, the
 * timezone) happens in the api with cron-parser so the 400 carries the
 * parser's message; this only rejects obvious garbage at the schema edge.
 */
const cronShape = z
    .string()
    .trim()
    .min(9, 'cron must have 5 or 6 whitespace-separated fields')
    .max(200)
    .refine((s) => {
    const fields = s.split(/\s+/).length;
    return fields === 5 || fields === 6;
}, { message: 'cron must have 5 or 6 whitespace-separated fields' });
export const CreateWorkflowScheduleInputSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    cron: cronShape,
    /** Defaults to UTC. */
    timezone: z.string().trim().min(1).max(100).optional(),
    /** Defaults to `{}`. */
    input: z.record(z.string(), z.unknown()).optional(),
    /** Site / customer tags (DEV-17502). Defaults to `{}` (org-wide). */
    tags: ScheduleTagsSchema.optional(),
    workflowVersionId: z.string().min(1).optional(),
    /** Defaults to true. */
    enabled: z.boolean().optional(),
    /** Defaults to true. */
    preventOverlap: z.boolean().optional(),
    /** Defaults to 5. 0 disables the circuit breaker. */
    maxConsecutiveFailures: z.number().int().min(0).max(1000).optional(),
})
    .strict();
/**
 * On a Formant-managed workflow's schedule only `cron`, `timezone` and
 * `enabled` are a person's: `input`, `name`, `workflowVersionId`,
 * `preventOverlap` and `maxConsecutiveFailures` answer 409
 * `managed-workflow-locked` through the public API (DEV-17471).
 */
export const UpdateWorkflowScheduleInputSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    cron: cronShape.optional(),
    timezone: z.string().trim().min(1).max(100).optional(),
    input: z.record(z.string(), z.unknown()).optional(),
    /** Replaces the tag set; `{}` clears it (DEV-17502). */
    tags: ScheduleTagsSchema.optional(),
    /** `null` clears the pin (fire the currentVersion). */
    workflowVersionId: z.string().min(1).nullable().optional(),
    enabled: z.boolean().optional(),
    preventOverlap: z.boolean().optional(),
    maxConsecutiveFailures: z.number().int().min(0).max(1000).optional(),
})
    .strict();
/**
 * Optional filters for `GET /v1/schedules` and `GET /v1/workflows/:id/schedules`.
 * Org scoping comes from the bearer token, never from the query string.
 * `tags` is the parsed form of the repeatable `tag=key:value` query param
 * (see {@link scheduleTagsMatch} for the subset rule); `untagged: false`
 * leaves the org-wide schedules out.
 */
export const ListWorkflowSchedulesQuerySchema = z
    .object({
    workflowId: z.string().min(1).optional(),
    enabled: z.boolean().optional(),
    tags: z.array(z.object({ key: z.string().min(1), value: z.string().min(1) })).optional(),
    untagged: z.boolean().optional(),
})
    .strict();
/** Run statuses that count as "still in flight" for `preventOverlap`. */
export const inFlightRunStatuses = ['PENDING', 'RUNNING', 'PAUSED'];
/**
 * What an in-process fire of a schedule answered, as the Daily Brief module
 * reports it (`POST /daily-brief/refresh`, `run.blocked` on the page).
 * `skipped-overlap`: a run of this schedule (or the org's share of this
 * template) is still in flight. `skipped-disabled`: the workflow or the
 * schedule is paused, or an operator switch is on. `skipped-breaker`: the
 * failure breaker switched the schedule off.
 */
export const FireWorkflowScheduleResultSchema = z.object({
    outcome: z.enum(['fired', 'skipped-overlap', 'skipped-disabled', 'skipped-breaker']),
    runId: z.string().nullable(),
});
/**
 * The one line a person reads under a schedule that is not firing. The
 * platform's reasons come from `pausedReason`; a pause a person asked for
 * carries none. Null while enabled.
 */
export function describePausedSchedule(schedule) {
    if (schedule.enabled)
        return null;
    switch (schedule.pausedReason) {
        case 'workflow-disabled':
            return 'Updates paused: workflow disabled';
        case 'repeated-errors':
            return 'Updates paused: repeated errors';
        case 'view-removed':
            return 'Updates paused: view removed';
        default:
            return 'Updates paused';
    }
}
//# sourceMappingURL=workflow-schedule.js.map